import React from 'react';

interface Player {
  number: number;
  name: string;
}

interface Penalty {
  id: string;
  label: string;
  time: string;
  player: Player;
  periodLabel?: string; // NEW: Optional period indicator like "(from Q1)"
}

interface PenaltiesSectionProps {
  usPenalties: Penalty[];
  themPenalties: Penalty[];
  onDeletePenalty: (side: 'US' | 'THEM', id: string) => void;
  onEndPenalty: (side: 'US' | 'THEM', id: string) => void;
  onAddPenalty: (side: 'US' | 'THEM') => void;
  submittingActionKey?: string | null;
}

function formatTime(time: string): string {
  // If already in mm:ss or hh:mm:ss, return as is
  if (time.includes(':')) return time;
  const totalSeconds = parseInt(time, 10);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const PenaltiesSection: React.FC<PenaltiesSectionProps> = ({
  usPenalties,
  themPenalties,
  onDeletePenalty,
  onEndPenalty,
  onAddPenalty,
  submittingActionKey = null,
}) => (
  <div className="z-10">
    <div className="relative text-2xl font-bold text-center">Penalties</div>
    <div className="relative flex w-full justify-between items-stretch gap-4 z-10 pt-3 px-3 h-[80x]">
      <div className="flex-1 flex flex-col items-center">
        <button
          className={`w-full font-bold rounded-lg px-3 xs:px-6 py-4 text-center flex justify-center items-center shadow-lg bg-[#7fe526] text-black transition-transform ${'cursor-pointer hover:scale-105'}`}
          onClick={() => onAddPenalty('US')}
        >
          <span className="text-xl sm:text-2xl">US</span>
        </button>
        {/* US Penalty Cards */}
        <div className="flex flex-col gap-2 mt-3 w-full items-start">
          {/* US Penalty Cards */}
          {usPenalties.map((penalty) => (
            <div
              key={penalty.id}
              className="bg-[#b71c1c] text-white rounded-lg shadow-lg px-4 py-3 flex flex-col w-full relative animate-fade-in"
            >
              <div className="font-bold text-base mb-1">{penalty.label}</div>
              <div className="text-base flex items-center justify-between gap-2">
                {formatTime(penalty.time)}
                {penalty.periodLabel && <div className="font-bold opacity-90">{penalty.periodLabel}</div>}
              </div>
              <div className="text-base text-[#b8e57d]">{penalty.player?.name}</div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <button
                  className={`bg-gray-300 text-black rounded px-3 py-1 font-semibold transition-colors ${
                    submittingActionKey === `penalty-delete-US-${penalty.id}`
                      ? 'cursor-not-allowed opacity-60'
                      : 'hover:bg-gray-400'
                  }`}
                  onClick={() => onDeletePenalty('US', penalty.id)}
                  disabled={submittingActionKey === `penalty-delete-US-${penalty.id}`}
                >
                  Delete
                </button>
                <button
                  className={`bg-gray-300 text-black rounded px-3 py-1 font-semibold transition-colors ${
                    submittingActionKey === `penalty-end-US-${penalty.id}`
                      ? 'cursor-not-allowed opacity-60'
                      : 'hover:bg-[#b71c1c]'
                  }`}
                  onClick={() => onEndPenalty('US', penalty.id)}
                  disabled={submittingActionKey === `penalty-end-US-${penalty.id}`}
                >
                  End
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center">
        <button
          className={`w-full font-bold rounded-lg px-3 xs:px-6 py-4 text-center flex justify-center items-center shadow-lg bg-[#294e87] text-white transition-transform ${
            submittingActionKey === 'penalty-add-THEM'
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer hover:scale-105'
          }`}
          onClick={() => onAddPenalty('THEM')}
          disabled={submittingActionKey === 'penalty-add-THEM'}
        >
          <span className="text-xl sm:text-2xl">THEM</span>
        </button>
        {/* THEM Penalty Cards */}
        <div className="flex flex-col gap-2 mt-3 w-full items-end">
          {/* THEM Penalty Cards */}
          {themPenalties.map((penalty) => (
            <div
              key={penalty.id}
              className="bg-[#b71c1c] text-white rounded-lg shadow-lg px-4 py-3 w-full flex flex-col relative animate-fade-in"
            >
              <div className="font-bold text-lg mb-1">{penalty.label}</div>
              <div className="text-base flex items-center justify-between gap-2">
                {formatTime(penalty.time)}
                {penalty.periodLabel && <div className="font-bold opacity-90">{penalty.periodLabel}</div>}
              </div>
              {/* <div className="text-base text-[#7fe526] text-left">
                {penalty.player?.name}
              </div> */}
              <div className="flex gap-2 mt-1 items-center justify-between">
                <button
                  className={`bg-gray-300 text-black rounded px-3 py-1 font-semibold transition-colors ${
                    submittingActionKey === `penalty-delete-THEM-${penalty.id}`
                      ? 'cursor-not-allowed opacity-60'
                      : 'hover:bg-gray-400'
                  }`}
                  onClick={() => onDeletePenalty('THEM', penalty.id)}
                  disabled={submittingActionKey === `penalty-delete-THEM-${penalty.id}`}
                >
                  Delete
                </button>
                <button
                  className={`bg-gray-300 text-black rounded px-3 py-1 font-semibold transition-colors ${
                    submittingActionKey === `penalty-end-THEM-${penalty.id}`
                      ? 'cursor-not-allowed opacity-60'
                      : 'hover:bg-[#b71c1c]'
                  }`}
                  onClick={() => onEndPenalty('THEM', penalty.id)}
                  disabled={submittingActionKey === `penalty-end-THEM-${penalty.id}`}
                >
                  End
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default PenaltiesSection;
